import React, { useState } from 'react';
import { addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, storage } from '../firebase.config.js';
import { useAgency } from '../AgencyContext';
import './AddTaskModal.css';

const AddTaskModal = ({ isOpen, onClose, selectedModel, aiModels, onTaskCreated }) => {
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskPhotoFile, setTaskPhotoFile] = useState(null);
  const [taskPhotoPreview, setTaskPhotoPreview] = useState('');
  const [hasL2Process, setHasL2Process] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [user] = useAuthState(auth);
  const { getAgencyCollection } = useAgency();

  const resetForm = () => {
    setTaskName('');
    setTaskDescription('');
    setTaskPhotoFile(null);
    setTaskPhotoPreview('');
    setHasL2Process(false);
    setIsVideo(false);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTaskPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setTaskPhotoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhotoToFirebase = async (file) => {
    if (!file || !user) return null;
    
    setUploadingPhoto(true);
    try {
      const timestamp = Date.now();
      const fileName = `task-photos/${user.uid}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskName.trim() || !selectedModel || !user) return;

    setLoading(true);

    try {
      const aitaskCollection = getAgencyCollection('aitask');
      if (!aitaskCollection) return;
      
      const selectedProfile = aiModels.find(model => model.id === selectedModel);
      
      let photoUrl = '';
      if (taskPhotoFile) {
        photoUrl = await uploadPhotoToFirebase(taskPhotoFile);
      }
      
      await addDoc(aitaskCollection, {
        name: taskName.trim(),
        description: taskDescription.trim() || '',
        photoUrl: photoUrl || '',
        aiModelId: selectedModel,
        profileName: selectedProfile?.name || '',
        profilePhotoUrl: selectedProfile?.photoURL || '',
        hasL2Process: hasL2Process,
        isVideo: isVideo,
        workflow: hasL2Process ? 'L1 → QA → L2 → QA' : 'L1 → QA',
        createdAt: serverTimestamp(),
        createdBy: user.email,
        status: 'active'
      });
      
      resetForm();
      onClose();
      if (onTaskCreated) onTaskCreated();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-task-modal">
      <div className="modal__overlay" onClick={handleClose}>
        <div className="modal__content" onClick={(e) => e.stopPropagation()}>
          <div className="modal__header">
            <h3 className="modal__title">Add New Task</h3>
            <button 
              onClick={handleClose}
              className="modal__close-btn"
              type="button"
            >
              ×
            </button>
          </div>
        
        <form onSubmit={handleSubmit} className="add-task-form">
          <div className="task-header-section">
            <div className="photo-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="form__file-input"
                id="task-photo-upload"
              />
              {taskPhotoPreview ? (
                <div className="photo-preview">
                  <img src={taskPhotoPreview} alt="Task preview" className="preview-image" />
                  <label htmlFor="task-photo-upload" className="file-upload-label">
                    Change
                  </label>
                </div>
              ) : (
                <label htmlFor="task-photo-upload" className="file-upload-label">
                  Choose Photo
                </label>
              )}
            </div>
            
            <div className="task-info-container">
              <input
                type="text"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                placeholder="Task name *"
                className="form__input task-name-input"
                required
              />
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="Task description..."
                className="form__textarea task-description-input"
                rows="3"
              />
            </div>
          </div>

          <div className="form__group">
            <div className="workflow__row">
              <div className="workflow__top">
                <div className="horizontal__switch">
                  <span className={`switch__option ${!hasL2Process ? 'active' : ''}`}>Quick</span>
                  <div 
                    className={`toggle__switch ${hasL2Process ? 'right' : 'left'}`}
                    onClick={() => setHasL2Process(!hasL2Process)}
                    type="button"
                  >
                    <div className="toggle__handle"></div>
                  </div>
                  <span className={`switch__option ${hasL2Process ? 'active' : ''}`}>Complete</span>
                </div>
                
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    checked={isVideo}
                    onChange={(e) => setIsVideo(e.target.checked)}
                  />
                  <span className="checkbox__mark"></span>
                  <span className="checkbox__text">Video</span>
                </label>
              </div>
              
              <div className="workflow__info">
                {hasL2Process ? 'L1 → QA → L2 → QA' : 'L1 → QA'}
              </div>
            </div>
          </div>

          <div className="modal__actions">
            <button 
              type="button"
              onClick={handleClose}
              className="modal__button modal__button--cancel"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading || uploadingPhoto || !taskName.trim()}
              className="modal__button modal__button--primary"
            >
              {loading || uploadingPhoto ? (
                <>
                  <div className="submit__spinner"></div>
                  {uploadingPhoto ? 'Uploading photo...' : 'Creating...'}
                </>
              ) : (
                'Add Task'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;